import { useEffect } from "react";
import analyticsService from "../AnalyticsService";
import { ANALYTICS_EVENTS, DEBOUNCE_MS } from "../constants";
import {
  beginInteraction,
  claimInteraction,
  finishInteraction,
  getControlLabel,
  getElementMetadata,
  getElementType,
  normalizeInteractionParams,
  recentSortHeader,
  rememberSortHeader,
  resetInteraction,
  toAnalyticsId,
  toParamValue,
} from "./interactionMetadata";
import { setupUIStateTracking } from "./uiStateTracker";

const INTERACTION_TRIGGER_POINT = "interaction_tracker";
const SCROLL_THRESHOLDS = [25, 50, 75, 90];

// Elements (or their descendants) carrying this attribute already report their
// own "ui_interaction" events (e.g. trackSelectInteraction/trackToggleInteraction),
// so the generic listener below must not double-report them.
const ANALYTICS_MANAGED_ATTR = "data-analytics-managed";

const SCROLL_TARGET_SELECTORS = [
  "#root",
  "#alldata",
  ".scrollableCategory",
  ".scrollableFilter",
  ".selectedFiltersBlockScroll",
  ".chat-bubble-scroll",
  ".sms-scroll-content",
  ".scroll-view",
  ".scrollToNotificationPanel",
  ".infinite-scroll-datatable-container",
  ".p-datatable-scrollable-body",
  ".p-datatable-scrollable-wrapper",
].join(", ");

const debounce = (fn, wait) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), wait);
  };
};

const getElementLabel = (target) => {
  if (!target) return undefined;

  return (
    target.dataset?.analyticsLabel ||
    target.getAttribute?.("aria-label") ||
    target.getAttribute?.("title") ||
    target.getAttribute?.("id") ||
    undefined
  );
};

const getPaginationAction = (target) => {
  const classes = `${target.className} ${target.querySelector("[class*='pi-angle']")?.className || ""}`;
  if (/first|angle-double-left/.test(classes)) return "first";
  if (/last|angle-double-right/.test(classes)) return "last";
  if (/prev|angle-left/.test(classes)) return "previous";
  if (/next|angle-right/.test(classes)) return "next";
  return "page";
};

const findClickTarget = (element) => {
  if (!(element instanceof Element)) return null;
  const control = element.closest(
    "button, a, [role='button'], [role='tab'], [role='menuitem'], " +
      "input, select, [role='option'], .p-dropdown-item, .p-multiselect-item, " +
      "[role='checkbox'], [role='radio'], [role='switch'], [role='combobox'], .p-checkbox, .p-radiobutton, .p-inputswitch, " +
      ".p-sortable-column",
  );
  if (control) return control;
  const target = element.closest(
    "[data-analytics-id]:not([data-analytics-type='dialog']):not([data-analytics-type='accordion']), .p-selectable-row, " +
      "tbody tr, [role='row'], .action-icon, .actionIcon, .cursor-pointer, tbody .pi",
  );
  if (target && getElementType(target) !== "table_row") return target;
  for (
    let current = element;
    current && current !== target && current !== document.body;
    current = current.parentElement
  ) {
    if (
      globalThis.getComputedStyle(current).cursor === "pointer" &&
      (current.style?.cursor === "pointer" ||
        globalThis.getComputedStyle(current.parentElement || document.body)
          .cursor !== "pointer")
    )
      return current;
  }
  return target;
};

const toNumber = (text) =>
  /^\d+$/.test(String(text ?? "").trim()) ? Number(text) : undefined;

// Page number, page count and page size as currently shown by a paginator.
const getPaginationState = (paginator) => {
  if (!paginator) return {};
  const report = Array.from(
    paginator.querySelectorAll(".p-paginator-current, span"),
  )
    .map((node) => node.textContent?.match(/(\d+)\s*(?:of|\/)\s*(\d+)/i))
    .find(Boolean);
  const active = paginator.querySelector(
    ".p-paginator-page.p-highlight, .page-item.active, [aria-current='page']",
  );
  const sizeControl = paginator.querySelector(
    ".p-dropdown-label, .p-paginator-rpp-options .p-dropdown-label, select",
  );
  return {
    page_number: toNumber(active?.textContent) ?? toNumber(report?.[1]),
    total_pages: toNumber(report?.[2]),
    page_size: toNumber(
      sizeControl?.matches("select")
        ? sizeControl.value
        : sizeControl?.textContent,
    ),
  };
};

const withoutUndefined = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );

const getSelectedValue = (target, type) => {
  if (type === "select-one" || type === "select-multiple") {
    return toParamValue(
      Array.from(target.selectedOptions || []).map(
        (option) => option.textContent || option.value,
      ),
    );
  }
  // Unnamed checkboxes have the browser default value "on"; use their label.
  return toParamValue(
    target.value && target.value !== "on"
      ? target.value
      : getControlLabel(target, type),
  );
};

const CLOSE_CONTROL =
  ".p-dialog-header-close, .p-sidebar-close, .btn-close, [data-bs-dismiss]";

// Generic openers/closers report the popup/accordion state they changed; links,
// rows, tabs, pagination and in-dialog actions (e.g. "Save") keep their own event.
const canReportStateChange = ({ action, element }, target, type) => {
  if (!["button", "action_icon"].includes(type)) return false;
  if (action !== "popup_closed") return true;
  return (
    !element.contains(target) ||
    target.matches(CLOSE_CONTROL) ||
    Boolean(target.closest(CLOSE_CONTROL)) ||
    /close|dismiss/i.test(
      `${target.getAttribute("aria-label") || ""} ${target.dataset.analyticsId || ""} ${target.dataset.analyticsLabel || ""}`,
    )
  );
};

const getScrollDepth = (target) => {
  const scrollContainer =
    target instanceof Element
      ? target
      : document.scrollingElement || document.documentElement;

  const scrollTop = scrollContainer.scrollTop || globalThis.scrollY || 0;
  const docHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;

  if (docHeight <= 0) return 100;
  return Math.min(100, Math.round((scrollTop / docHeight) * 100));
};

const getScrollTargetName = (target) => {
  if (!(target instanceof Element)) return "window";

  return target.id || "container";
};

const isScrollableElement = (element) => {
  if (!(element instanceof HTMLElement)) return false;

  const style = globalThis.getComputedStyle?.(element);
  const overflowY = style?.overflowY || "";
  const overflowX = style?.overflowX || "";
  const isOverflowing =
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth;

  return (
    (isOverflowing && /(auto|scroll|overlay)/i.test(overflowY)) ||
    /(auto|scroll|overlay)/i.test(overflowX)
  );
};

export const useInteractionTracker = () => {
  useEffect(() => {
    let disposed = false;
    const handledNativeEvents = new WeakSet();

    const markHandled = (event) => {
      if (!event || typeof event !== "object") return false;
      if (handledNativeEvents.has(event)) return true;
      handledNativeEvents.add(event);
      return false;
    };

    const trackInteraction = (interactionType, params = {}) => {
      if (disposed) return;
      analyticsService.track(
        ANALYTICS_EVENTS.UI_INTERACTION,
        normalizeInteractionParams({
          ...params,
          trigger_point: INTERACTION_TRIGGER_POINT,
          interaction_type: interactionType,
        }),
        { dedupe: false },
      );
    };

    const onClick = (event) => {
      if (markHandled(event)) return;
      // A new gesture starts; untracked clicks (e.g. backdrop, accordion header)
      // must let their resulting state changes be reported.
      resetInteraction();

      const target = findClickTarget(event.target);
      if (!target) return;
      if (
        target.closest(`[${ANALYTICS_MANAGED_ATTR}="true"]`) ||
        target.matches(":disabled, [aria-disabled='true'], .p-disabled") ||
        target.closest(".disabled-menu, .p-disabled, [aria-disabled='true']")
      )
        return;

      const type = getElementType(target);
      if (type === "table_row" && target.closest("thead")) return;
      if (type === "sort")
        rememberSortHeader(
          target.closest("th, [role='columnheader']") || target,
        );
      if (
        target.matches("[role='option'], .p-dropdown-item, .p-multiselect-item")
      )
        return;
      // These controls report actual state changes rather than click intent.
      if (
        [
          "sort",
          "accordion",
          "checkbox",
          "radio",
          "toggle",
          "input",
          "search_input",
        ].includes(type)
      )
        return;
      if (type === "select") return;
      if (
        type === "pagination" &&
        target.matches(".p-dropdown, .p-multiselect")
      )
        return;
      const interaction = beginInteraction(target, event);
      const pageParams = analyticsService.getDefaultEventParams();
      const metadata = getElementMetadata(target);
      const contentId = target.getAttribute("id") || undefined;
      const paginator = target.closest(".p-paginator, .pagination");
      // Macrotask: runs after every listener of this click (including React's
      // onClick), so explicit trackers can claim the gesture first.
      setTimeout(() => {
        if (disposed) {
          finishInteraction(interaction);
          return;
        }
        if (!interaction.claimed) {
          const change = interaction.stateChange;
          if (change && canReportStateChange(change, target, type)) {
            trackInteraction(change.action, {
              ...pageParams,
              ...change.params,
              ...metadata,
              interaction_source:
                event.detail === 0 ? "keyboard_or_programmatic" : "mouse_click",
            });
            finishInteraction(interaction);
            return;
          }
          let action =
            type === "table_row"
              ? "row_clicked"
              : type === "link" || type === "menu_item"
                ? "navigation_clicked"
                : "clicked";
          const params = {
            ...pageParams,
            ...metadata,
            interaction_source:
              event.detail === 0 ? "keyboard_or_programmatic" : "mouse_click",
          };
          if (metadata.element_type === "pagination") {
            action = "page_changed";
            const pageAction = getPaginationAction(target);
            Object.assign(
              params,
              getElementMetadata(target, {
                element_id: `pagination_${pageAction}`,
                element_label: `${pageAction === "page" ? "Select" : pageAction} page`,
                element_type: "pagination",
              }),
              { page_action: pageAction },
            );
            // Read after handlers ran so next/previous report the new page.
            const pageState = getPaginationState(paginator);
            Object.assign(
              params,
              withoutUndefined({
                ...pageState,
                page_number:
                  toNumber(target.textContent) ?? pageState.page_number,
              }),
            );
          }
          if (type === "menu_item" || type === "link" || type === "tab") {
            analyticsService.track(
              ANALYTICS_EVENTS.SELECT_CONTENT,
              normalizeInteractionParams({
                ...params,
                content_type: type,
                content_id: contentId,
                interaction_type: "selected",
                trigger_point: INTERACTION_TRIGGER_POINT,
              }),
              { dedupe: false },
            );
          } else {
            trackInteraction(action, params);
          }
        }
        finishInteraction(interaction);
      }, 0);
    };

    const onChange = (event) => {
      if (markHandled(event)) return;

      const target = event.target;
      if (!target) return;
      if (target.closest?.(`[${ANALYTICS_MANAGED_ATTR}="true"]`)) return;
      if (target.closest?.(".p-dropdown, .p-multiselect")) return;

      const type = target.type || target.tagName?.toLowerCase();

      if (
        ["select-one", "select-multiple", "checkbox", "radio"].includes(type)
      ) {
        const pageSize =
          target.closest(".p-paginator, .pagination") &&
          target.tagName === "SELECT";
        trackInteraction(
          pageSize
            ? "page_size_changed"
            : type === "checkbox" || type === "radio"
              ? "toggled"
              : "selection_changed",
          {
            ...getElementMetadata(target),
            ...(pageSize && {
              element_type: "pagination",
              page_size: Number(target.value),
            }),
            input_type: type,
            input_name:
              target.dataset?.analyticsInputName || getElementLabel(target),
            ...(type === "checkbox" && {
              checkbox_id:
                target.getAttribute("id") ||
                target.closest(".p-checkbox")?.getAttribute("id") ||
                "-",
            }),
            selected_value: getSelectedValue(target, type),
            ...(type === "select-multiple" && {
              selected_count: target.selectedOptions?.length || 0,
            }),
            checked:
              typeof target.checked === "boolean" ? target.checked : undefined,
          },
        );
      }
    };

    const searchTimers = new Map();
    const onInput = (event) => {
      const target = event.target;
      if (
        getElementType(target) !== "search_input" ||
        target.closest?.(`[${ANALYTICS_MANAGED_ATTR}="true"]`)
      )
        return;
      clearTimeout(searchTimers.get(target));
      const searchTerm = toParamValue(target.value);
      const metadata = getElementMetadata(target);
      searchTimers.set(
        target,
        setTimeout(() => {
          searchTimers.delete(target);
          trackInteraction(searchTerm ? "search_changed" : "search_cleared", {
            ...metadata,
            search_context: getControlLabel(target, "search_input") || undefined,
            ...(searchTerm && {
              search_term: searchTerm,
              search_term_length: target.value.trim().length,
            }),
          });
        }, 500),
      );
    };

    const onCopy = (event) => {
      if (markHandled(event)) return;

      trackInteraction("copied", {
        ...getElementMetadata(
          event.target instanceof Element ? event.target : null,
          { element_type: "document", interaction_type: "copied" },
        ),
        action: "copy",
      });
    };
    const onPaste = (event) => {
      if (markHandled(event)) return;

      trackInteraction("pasted", {
        ...getElementMetadata(
          event.target instanceof Element ? event.target : null,
          { element_type: "document", interaction_type: "pasted" },
        ),
        action: "paste",
      });
    };
    const onPrint = (event) => {
      if (markHandled(event)) return;

      trackInteraction("printed", {
        ...getElementMetadata(null, {
          element_type: "document",
          element_id: "document_print",
          element_label: "Print document",
        }),
        action: "print",
      });
    };

    const scrollThresholdsByTarget = new WeakMap();

    const trackScrollThresholds = (currentTarget) => {
      const scrollDepth = getScrollDepth(currentTarget);
      const scrollTarget = getScrollTargetName(currentTarget);
      const targetKey =
        currentTarget instanceof Element
          ? currentTarget
          : document.documentElement;
      const sentThresholds =
        scrollThresholdsByTarget.get(targetKey) || new Set();

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (scrollDepth < threshold || sentThresholds.has(threshold)) {
          return;
        }

        sentThresholds.add(threshold);
        trackInteraction("scroll_depth", {
          ...getElementMetadata(
            currentTarget instanceof Element ? currentTarget : null,
            {
              element_type: "scroll_container",
              element_id: `${scrollTarget}_scroll`,
              element_label: "Scroll depth",
            },
          ),
          scroll_depth: threshold,
          scroll_depth_actual: scrollDepth,
          scroll_target: scrollTarget,
        });
      });

      scrollThresholdsByTarget.set(targetKey, sentThresholds);
    };

    const onScroll = debounce((event) => {
      if (markHandled(event)) return;

      const currentTarget = event?.target || event?.currentTarget;
      trackScrollThresholds(currentTarget);
    }, DEBOUNCE_MS.SCROLL);

    const attachedTargets = new Set();
    let refreshTimer = null;

    const attachTarget = (element) => {
      if (!(element instanceof Element) || attachedTargets.has(element)) return;

      element.addEventListener("scroll", onScroll, { passive: true });
      attachedTargets.add(element);
    };

    const scanForScrollTargets = () => {
      const candidateTargets = new Set([
        document.scrollingElement,
        document.documentElement,
        document.body,
        ...document.querySelectorAll(SCROLL_TARGET_SELECTORS),
      ]);

      document.querySelectorAll("*").forEach((element) => {
        if (isScrollableElement(element)) {
          candidateTargets.add(element);
        }
      });

      candidateTargets.forEach(attachTarget);
    };

    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(scanForScrollTargets, 0);
    };

    const mutationObserver = new MutationObserver(scheduleRefresh);

    const cleanupUIStates = setupUIStateTracking(trackInteraction);
    globalThis.addEventListener("keydown", resetInteraction, {
      capture: true,
      passive: true,
    });
    globalThis.addEventListener("click", onClick, {
      capture: true,
      passive: true,
    });
    globalThis.addEventListener("change", onChange, {
      capture: true,
      passive: true,
    });
    globalThis.addEventListener("input", onInput, {
      capture: true,
      passive: true,
    });
    globalThis.addEventListener("copy", onCopy);
    globalThis.addEventListener("paste", onPaste);
    globalThis.addEventListener("beforeprint", onPrint);
    document.addEventListener("scroll", onScroll, true);
    scanForScrollTargets();
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "id", "style"],
    });

    return () => {
      disposed = true;
      globalThis.removeEventListener("keydown", resetInteraction, true);
      globalThis.removeEventListener("click", onClick, true);
      globalThis.removeEventListener("change", onChange, true);
      globalThis.removeEventListener("input", onInput, true);
      cleanupUIStates();
      searchTimers.forEach(clearTimeout);
      globalThis.removeEventListener("copy", onCopy);
      globalThis.removeEventListener("paste", onPaste);
      globalThis.removeEventListener("beforeprint", onPrint);
      document.removeEventListener("scroll", onScroll, true);
      mutationObserver.disconnect();
      clearTimeout(refreshTimer);

      attachedTargets.forEach((element) => {
        element.removeEventListener("scroll", onScroll);
      });
    };
  }, []);
};

// Spread this on a custom select/toggle component's root element so the
// generic change listener skips it (pairs with trackSelectInteraction/trackToggleInteraction).
export const analyticsManagedProps = { [ANALYTICS_MANAGED_ATTR]: "true" };

const stopNativeEventPropagation = (event) => {
  const nativeEvent = event?.originalEvent || event?.nativeEvent;
  nativeEvent?.stopPropagation?.();
  nativeEvent?.stopImmediatePropagation?.();
};

export const trackTableSortInteraction = (event, tableId) => {
  claimInteraction();
  const field = toAnalyticsId(event?.sortField || "column");
  const header = recentSortHeader();
  const column =
    (header && getControlLabel(header, "sort")) || field.replace(/_/g, " ");
  analyticsService.track(
    ANALYTICS_EVENTS.UI_INTERACTION,
    normalizeInteractionParams({
      ...getElementMetadata(header, {
        element_id:
          header?.dataset?.analyticsId ||
          header?.id ||
          `${toAnalyticsId(tableId)}_sort_${field}`,
        element_label: header?.dataset?.analyticsLabel || column,
        element_type: "sort",
      }),
      trigger_point: INTERACTION_TRIGGER_POINT,
      interaction_type: "sort_changed",
      sort_field: field,
      sort_column: column,
      sort_direction:
        event?.sortOrder === 1
          ? "ascending"
          : event?.sortOrder === -1
            ? "descending"
            : "none",
    }),
    { dedupe: false },
  );
};

export const trackSelectInteraction = (event, ...interactionArgs) => {
  const [selectedValue, , overrides = {}] = interactionArgs;
  stopNativeEventPropagation(event);
  claimInteraction();
  const target = event?.target ?? event;
  const element =
    (target instanceof Element ? target : target?.element) ||
    (target?.id && document.getElementById(target.id)) ||
    event?.originalEvent?.target?.closest?.(
      "select, .p-dropdown, .p-multiselect, [role='combobox']",
    );
  const input_name =
    target?.id || target?.name || getElementLabel(element);
  const value = selectedValue ?? event?.value;

  analyticsService.track(
    ANALYTICS_EVENTS.UI_INTERACTION,
    normalizeInteractionParams({
      ...getElementMetadata(element, {
        element_type: "select",
        element_id: target?.id,
      }),
      trigger_point: INTERACTION_TRIGGER_POINT,
      interaction_type: "selection_changed",
      input_type: "select",
      input_name,
      ...withoutUndefined({
        selected_value: toParamValue(value),
        selected_count: Array.isArray(value) ? value.length : undefined,
      }),
      ...overrides,
    }),
    { dedupe: false },
  );
};

export const trackToggleInteraction = (event, checked, overrides = {}) => {
  stopNativeEventPropagation(event);
  claimInteraction();
  const target = event?.target ?? event;
  const input_name =
    target?.id || target?.name || getElementLabel(target?.element);

  analyticsService.track(
    ANALYTICS_EVENTS.UI_INTERACTION,
    normalizeInteractionParams({
      ...getElementMetadata(target?.element || event?.originalEvent?.target, {
        element_type: "toggle",
        element_id: input_name,
      }),
      trigger_point: INTERACTION_TRIGGER_POINT,
      interaction_type: "toggled",
      input_type: "toggle",
      input_name,
      checked: Boolean(checked),
      ...overrides,
    }),
    { dedupe: false },
  );
};

export default useInteractionTracker;
